import { useState } from 'react'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import type { ContactFormKeys } from '../schemas/contactSchema'
import { FIAT_SECTION_FIELDS } from '../utils/constants'

interface UseContactFormSectionsProps {
  requiredFields?: ContactFormKeys[]
  isCryptoFlow: boolean
  isFiatSectionInitiallyOn: boolean
  hasGovernmentIds: boolean
  onResetFiatSection: () => void
  onAddGovernmentId: () => void
  onResetGovernmentIds: () => void
}

export const useContactFormSections = ({
  requiredFields,
  isCryptoFlow,
  isFiatSectionInitiallyOn,
  hasGovernmentIds,
  onResetFiatSection,
  onAddGovernmentId,
  onResetGovernmentIds,
}: UseContactFormSectionsProps) => {
  const { enableGovernmentIds } = useFeatureFlags()

  const [isFiatSectionToggledOn, setIsFiatSectionToggledOn] = useState(isFiatSectionInitiallyOn)
  const [isGovernmentIdsToggledOn, setIsGovernmentIdsToggledOn] = useState(hasGovernmentIds)

  const isFiatSectionLocked =
    isCryptoFlow || !!requiredFields?.some(field => FIAT_SECTION_FIELDS.includes(field))
  const hasGovernmentIdsRequired = !!requiredFields?.includes('governmentIds')

  const showFiatSection = isFiatSectionToggledOn || isFiatSectionLocked
  const showGovernmentIds = isGovernmentIdsToggledOn || hasGovernmentIdsRequired
  const showGovernmentIdsToggle = enableGovernmentIds || hasGovernmentIdsRequired

  const onToggleFiatSection = (value: boolean) => {
    if (!value && isFiatSectionLocked) return
    setIsFiatSectionToggledOn(value)
    if (!value) onResetFiatSection()
  }

  const onToggleGovernmentIds = (value: boolean) => {
    if (!value && hasGovernmentIdsRequired) return
    setIsGovernmentIdsToggledOn(value)
    if (value && !hasGovernmentIds) onAddGovernmentId()
    if (!value) onResetGovernmentIds()
  }

  return {
    showFiatSection,
    showGovernmentIds,
    showGovernmentIdsToggle,
    isFiatSectionLocked,
    hasGovernmentIdsRequired,
    onToggleFiatSection,
    onToggleGovernmentIds,
  }
}
